import Avatar from 'components/core/avatar'
import Checkbox from 'components/core/checkbox'
import { DraggableTableRow } from 'components/core/table-row'
import ActiveTabMenu from 'components/elements/dropdown/menus/active-tab-menu'
import ArchivedTabMenu from 'components/elements/dropdown/menus/archived-tab-menu'
import DraftTabMenu from 'components/elements/dropdown/menus/draft-tab-menu'
import { Ticket } from 'interfaces/ticket'
import { hasOnlyFalsyValues } from 'lib/object/utils'
import { Draggable, DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'

import ProjectsPopover from 'components/elements/projects/projects-popover'
import UserAvatar from 'components/elements/user-avatar'
import { EmptySearchScreen, LoadingScreenTableRow } from 'components/pages/requests/empty-screens'
import { useSingleQSelectedTagsContext } from 'components/pages/requests/single-q-selected-tags-provider'
import { CompanyTag, TaggableTag } from 'lib/api/company-tags/company-tags'
import { TicketStatusBadge } from 'lib/components/badge/badge'
import { isStatusCompleted, isStatusOnDeck } from 'lib/ticket/utils'
import { ChangeTagAction } from 'providers/company-tagging-provider'
import { useConfirmationModalContext } from 'providers/confirmation-modal-provider'
import { useSingleQueueContext } from 'providers/single-queue-provider'
import RatingRow from './rating-row'
import TicketLinkElement from './ticket-link-element'
import { useCallback, useState } from 'react'
import { Project } from 'interfaces/project'
import { updateTicket } from 'lib/api/tickets/tickets'
import { toast } from 'lib/components/toast/toast'
import { IconFlyoutMenu } from 'lib/components/dropdown-icon-menu/icon-flyout-menu'
import { PlusIcon, TicketIcon } from 'lucide-react'
import { StateScreen } from 'lib/components/state-screen/state-screen'

interface TicketRowProps {
  providedDragHandleProps?: DraggableProvidedDragHandleProps
  ticket: Ticket
  priorityMode?: boolean
  updateSelectedTags?: (updatedTag: CompanyTag, action: ChangeTagAction, taggableId: number) => void
  selectedTags?: TaggableTag[]
}

export const TicketRow = ({
  priorityMode,
  providedDragHandleProps,
  ticket,
  updateSelectedTags = () => null,
}: TicketRowProps): JSX.Element => {
  const [isProjectPopoverOpen, setIsProjectPopoverOpen] = useState(false)
  const {
    activeTab,
    dragDisable,
    bulkSelect,
    bulkStateSelected,
    bulkActionIds,
    priorityModeEnabled,
    userCanSort,
    fetchTickets,
    setBulkActionIds,
    setBulkStateSelected,
    updateTicket: updateTicketsList,
  } = useSingleQueueContext()
  const { selectedTags } = useSingleQSelectedTagsContext()

  const { menuButtonActionWithCallbacks } = useConfirmationModalContext()

  const {
    category,
    designer,
    filesDownloadUrl,
    friendlyStatusName,
    id,
    lastUpdatedForUser,
    position,
    project,
    subject,
    ticketFilesCount,
    ticketId,
    url,
    user,
  } = ticket

  const menuButtonAction = menuButtonActionWithCallbacks(fetchTickets, () => {
    setBulkActionIds([])
    setBulkStateSelected(null)
  })

  const BulkCheckbox = () => {
    const bulkID = ticketId || id

    return (
      <td>
        {!priorityMode && (
          <Checkbox
            id={bulkID.toString()}
            checked={bulkActionIds.some((bulkId) => bulkId.ticketId === bulkID || bulkId.requestId === bulkID)}
            label={''}
            disabled={bulkStateSelected !== null && bulkStateSelected !== friendlyStatusName}
            onChange={(event) => {
              bulkSelect(event, bulkID, friendlyStatusName, id)
            }}
          />
        )}
      </td>
    )
  }

  const updateTicketProject = useCallback(
    async (project: Project | null) => {
      try {
        const response = await updateTicket(Number(ticketId || id), { project_id: project?.id || null })
        updateTicketsList(response.ticket as Ticket)
        setIsProjectPopoverOpen(false)
        toast.success('Ticket project updated')
      } catch (error) {
        toast.error('Failed to update ticket project')
        console.error('Failed to update ticket project', error)
      }
    },
    [id, ticketId, updateTicketsList],
  )

  return (
    <>
      {activeTab === 'active' && (
        <td
          className="active:focus:tw-peppercorn-200 tw-whitespace-normal tw-break-normal tw-px-6 tw-py-4 tw-pl-4 tw-text-peppercorn-100"
          {...providedDragHandleProps}
        >
          {userCanSort && !(dragDisable || !isStatusOnDeck(friendlyStatusName)) && (
            <span data-testid="drag-handle">
              <i className="fas fa-grip-vertical tw-color-peppercorn-50"></i>
            </span>
          )}
        </td>
      )}

      {activeTab === 'draft' && <td className="tw-px-6 tw-py-4 tw-pl-4"></td>}
      {activeTab === 'archived' && <td className="tw-px-6 tw-py-4 tw-pl-4"></td>}
      {activeTab !== 'archived' && <BulkCheckbox />}
      {activeTab === 'active' && (
        <td className="tw-whitespace-normal tw-break-normal tw-px-6 tw-py-4 tw-pl-4 tw-text-xs tw-font-semibold">
          {position}
        </td>
      )}

      <td className="tw-break-words tw-px-6 tw-py-4 tw-pl-4 tw-text-sm tw-text-gray-900 [word-break:break-word]">
        {priorityMode ? (
          ticket.subject
        ) : (
          <TicketLinkElement
            ticket={ticket}
            showTags={true}
            selectedTags={selectedTags?.filter((tag) => tag.taggableId === Number(ticket.ticketId || ticket.id))}
            updateSelectedTags={updateSelectedTags}
          />
        )}
      </td>

      <td className="tw-min-w-[130px] tw-whitespace-normal tw-break-normal tw-border tw-px-6 tw-py-4 tw-pl-4">
        <TicketStatusBadge status={friendlyStatusName}></TicketStatusBadge>
      </td>
      <td>
        <ProjectsPopover
          isOpen={isProjectPopoverOpen}
          setIsOpen={setIsProjectPopoverOpen}
          project={project}
          onSelectProject={updateTicketProject}
        />
      </td>
      <td className="tw-font-gray-700 tw-whitespace-normal tw-break-normal tw-px-6 tw-py-4 tw-pl-4 tw-text-xs tw-font-medium">
        {category}
      </td>
      {activeTab === 'archived' && (
        <td className="tw-py-2 tw-pl-4 tw-pr-2">
          <RatingRow ticket={ticket} />
        </td>
      )}
      {activeTab === 'active' && (
        <td className="tw-whitespace-normal tw-break-normal tw-px-6 tw-py-4 tw-pl-4 tw-text-xs">
          <UserAvatar
            priorityModeEnabled={priorityModeEnabled}
            name={designer?.name}
            avatar={designer?.avatar}
            isDesigner={true}
          />
        </td>
      )}
      <td className="tw-whitespace-normal tw-break-normal tw-px-6 tw-py-4 tw-pl-4 tw-text-xs">
        <div className="tw-flex tw-items-center">
          <div className="tw-h-8 tw-w-8 tw-flex-shrink-0">
            <Avatar src={user?.avatar} name={user?.fullName} />
          </div>
          <div className="tw-ml-4">
            <div className="tw-text-xs tw-font-medium tw-text-gray-700">{user?.fullName}</div>
          </div>
        </div>
      </td>
      {activeTab !== 'draft' && (
        <td className="tw-whitespace-normal tw-break-normal tw-px-6 tw-py-4 tw-pl-4 tw-text-xs tw-font-semibold">
          {lastUpdatedForUser}
        </td>
      )}
      <td className="tw-w-12">
        {!priorityMode && (
          <IconFlyoutMenu icon={['far', 'ellipsis-v']} color="transparent">
            {activeTab === 'active' && (
              <ActiveTabMenu
                id={id}
                subject={subject}
                currentVersion={ticket.currentVersion}
                filesDownloadUrl={filesDownloadUrl}
                status={friendlyStatusName}
                userId={user?.id}
                menuActionBtn={menuButtonAction}
                ticketFilesCount={ticketFilesCount}
              />
            )}
            {activeTab === 'draft' && (
              <DraftTabMenu
                requestId={id}
                url={url}
                subject={subject}
                userId={user.id}
                menuActionBtn={menuButtonAction}
              />
            )}
            {activeTab === 'archived' && (
              <ArchivedTabMenu
                id={id}
                currentVersion={ticket.currentVersion}
                filesDownloadUrl={filesDownloadUrl}
                menuActionBtn={menuButtonAction}
                status={friendlyStatusName}
                subject={subject}
                ticketFilesCount={ticketFilesCount}
                userId={user?.id}
              />
            )}
          </IconFlyoutMenu>
        )}
      </td>
    </>
  )
}

const TableContent = (): JSX.Element => {
  const { dragDisable, tickets, fetchTickets } = useSingleQueueContext()
  const { setSelectedTags } = useSingleQSelectedTagsContext()
  const onDeckTickets = tickets.filter((ticket) => isStatusOnDeck(ticket.friendlyStatusName))
  const immovableTicketsAboveOnDeck = tickets.filter(
    ({ friendlyStatusName }) => !isStatusOnDeck(friendlyStatusName) && !isStatusCompleted(friendlyStatusName),
  )
  const immovableTicketsBelowOnDeck = tickets.filter(
    ({ friendlyStatusName }) => !isStatusOnDeck(friendlyStatusName) && isStatusCompleted(friendlyStatusName),
  )

  function selectTag(updatedTag: CompanyTag, taggableId: number) {
    setSelectedTags((prevTags) => {
      return [...prevTags, { ...updatedTag, taggableId }] as TaggableTag[]
    })
  }

  function unselectTag(updatedTag: CompanyTag, taggableId: number) {
    setSelectedTags((prevTags) => {
      return prevTags.filter(
        (tag) => (tag.taggableId === taggableId && tag.id !== updatedTag.id) || tag.taggableId !== taggableId,
      )
    })
  }

  function updateSelectedTags(updatedTag: CompanyTag, action: ChangeTagAction, taggableId: number) {
    switch (action) {
      case ChangeTagAction.Select:
        selectTag(updatedTag, taggableId)
        break
      case ChangeTagAction.Unselect:
        unselectTag(updatedTag, taggableId)
        break
      case ChangeTagAction.Update:
      case ChangeTagAction.Delete:
        fetchTickets()
        break
      default:
        break
    }
  }

  return (
    <>
      {immovableTicketsAboveOnDeck.map((ticket) => {
        return (
          <tr
            key={ticket.id}
            className="tw-relative tw-h-12 tw-whitespace-normal tw-border tw-border-solid tw-bg-white"
          >
            <TicketRow ticket={ticket} updateSelectedTags={updateSelectedTags} />
          </tr>
        )
      })}
      {onDeckTickets.map((ticket, index) => {
        return (
          <Draggable
            key={ticket.id.toString()}
            draggableId={ticket.id.toString()}
            index={index}
            isDragDisabled={dragDisable}
          >
            {(provided) => (
              <DraggableTableRow innerRef={provided.innerRef} draggableProvided={provided}>
                <TicketRow
                  providedDragHandleProps={provided.dragHandleProps}
                  ticket={ticket}
                  updateSelectedTags={updateSelectedTags}
                />
              </DraggableTableRow>
            )}
          </Draggable>
        )
      })}
      {immovableTicketsBelowOnDeck.map((ticket) => {
        return (
          <tr
            key={ticket.id}
            className="tw-relative tw-h-12 tw-whitespace-normal tw-border tw-border-solid tw-bg-white"
          >
            <TicketRow key={ticket.id} ticket={ticket} updateSelectedTags={updateSelectedTags} />
          </tr>
        )
      })}
    </>
  )
}

const PriorityModeTableContent = (): JSX.Element => {
  const { tickets } = useSingleQueueContext()

  return (
    <>
      {tickets.map((ticket, index) => {
        return (
          <Draggable key={ticket.id.toString()} draggableId={ticket.id.toString()} index={index} isDragDisabled={false}>
            {(provided) => (
              <DraggableTableRow innerRef={provided.innerRef} draggableProvided={provided}>
                <TicketRow providedDragHandleProps={provided.dragHandleProps} ticket={ticket} priorityMode />
              </DraggableTableRow>
            )}
          </Draggable>
        )
      })}
    </>
  )
}

const emptyStateMap = {
  active: {
    title: "You don't have any active requests yet.",
    subTitle:
      'Get started with a new design request. Share your details, collaborate with your designer, and bring your vision to life.',
  },
  archived: {
    title: "You don't have any archived requests yet.",
    subTitle:
      'Your completed and archived requests live here. Duplicate any request to pick up where you left off and create a new one in just a few clicks.',
  },
  draft: {
    title: "You don't have any draft requests yet.",
    subTitle:
      "Start creating design requests and save them as drafts to revisit later. Perfect for when you don't have all the details yet or aren't ready to send them to your designer.",
  },
}

const TicketListTableContent = ({ priorityMode = false }: { priorityMode?: boolean }): JSX.Element => {
  const { activeTab, tickets, loading, filters } = useSingleQueueContext()

  if (loading) {
    return <LoadingScreenTableRow />
  }

  if (tickets.length === 0 && hasOnlyFalsyValues(filters)) {
    return (
      <td colSpan={12} className="tw-h-[60vh] tw-text-center">
        <StateScreen
          Icon={TicketIcon}
          title={emptyStateMap[activeTab].title}
          subTitle={emptyStateMap[activeTab].subTitle}
          buttons={[
            {
              text: 'Create a new request',
              url: '/create',
              Icon: PlusIcon,
            },
          ]}
        />
      </td>
    )
  }

  if (tickets.length === 0 && !hasOnlyFalsyValues(filters)) {
    return <EmptySearchScreen />
  }

  if (priorityMode) {
    return <PriorityModeTableContent />
  }

  return <TableContent />
}

export default TicketListTableContent
